import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  addTableColumn,
  addTableHeader,
  addTableRow,
  applyTableDataToNode,
  CHECKBOX_CHECKED,
  CHECKBOX_UNCHECKED,
  columnMetricValue,
  DEFAULT_COL_WIDTH,
  defaultSummaryColumns,
  deleteTableColumn,
  deleteTableRow,
  formatNumber,
  getColWidths,
  MAX_COL_WIDTH,
  MIN_COL_WIDTH,
  setTableColWidth,
  SUMMARY_METRICS,
  summarizeTable,
} from '../../utils/noteTableUtils';

const parseCheckCell = (value) => {
  const match = String(value ?? '').match(/^\s*(☑|☐|✓|✔|\[x\]|\[X\]|\[\s*\])\s*(.*)$/i);
  if (!match) return null;
  return {
    checked: /☑|✓|✔|\[x\]/i.test(match[1]),
    text: match[2] || '',
  };
};

const encodeCheckCell = (checked, text) => (
  `${checked ? CHECKBOX_CHECKED : CHECKBOX_UNCHECKED}${text ? ` ${text}` : ''}`
);

const NoteTableWorkspaceModal = ({ tableNode, onClose, onCommit }) => {
  const [draft, setDraft] = useState({ rows: [['']] });
  const [active, setActive] = useState({ r: 0, c: 0 });
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    if (!tableNode) return undefined;
    let data = { rows: [['']] };
    try {
      const parsed = JSON.parse(tableNode.getAttribute('data-table') || '{}');
      if (Array.isArray(parsed?.rows) && parsed.rows.length) data = parsed;
    } catch {
      data = { rows: [['']] };
    }
    const summary = summarizeTable(data);
    const summaryColumns = data.summaryColumns
      ? data.summaryColumns
      : defaultSummaryColumns(summary);
    const next = { ...data, summaryColumns };
    setDraft(next);
    applyTableDataToNode(tableNode, next);
    onCommit?.();
    return undefined;
    // Opened table node only; parent commit stays stable enough for one write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableNode]);

  const persist = (next) => {
    draftRef.current = next;
    setDraft(next);
    if (tableNode) applyTableDataToNode(tableNode, next);
    onCommit?.();
  };

  const summary = useMemo(() => summarizeTable(draft), [draft]);
  const summaryColumns = draft?.summaryColumns && Object.keys(draft.summaryColumns).length
    ? draft.summaryColumns
    : defaultSummaryColumns(summary);

  const updateCell = (rowIndex, colIndex, value) => {
    const rows = draft.rows.map((row, r) => (
      r === rowIndex
        ? row.map((cell, c) => (c === colIndex ? value : cell))
        : [...row]
    ));
    persist({ ...draft, rows });
  };

  const setColumnMetric = (colIndex, metric) => {
    persist({
      ...draft,
      summaryColumns: { ...summaryColumns, [String(colIndex)]: metric },
    });
  };

  const toggleColumn = (colIndex, enabled) => {
    const next = { ...summaryColumns };
    if (enabled) next[String(colIndex)] = next[String(colIndex)] || 'sum';
    else delete next[String(colIndex)];
    persist({ ...draft, summaryColumns: next });
  };

  const rows = Array.isArray(draft?.rows) ? draft.rows : [['']];
  const colCount = Math.max(1, rows.reduce((max, row) => Math.max(max, (row || []).length), 1));
  const colWidths = getColWidths(draft);
  const hasHeader = draft?.hasHeader === true;
  const tableWidth = colWidths.reduce((sum, width) => sum + width, 0);

  const startResize = (event, colIndex) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startW = colWidths[colIndex] || DEFAULT_COL_WIDTH;
    const onMove = (moveEvent) => {
      const width = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, Math.round(startW + moveEvent.clientX - startX)));
      const next = setTableColWidth(draftRef.current, colIndex, width);
      draftRef.current = next;
      setDraft(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      persist(draftRef.current);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const appliedGrand = summary.columns.reduce((total, col) => {
    const metric = summaryColumns[String(col.index)];
    if (!metric) return total;
    const value = columnMetricValue(col, metric === 'count' ? 'sum' : metric);
    return metric === 'count' || metric === 'avg' || metric === 'min' || metric === 'max'
      ? total
      : total + (Number.isFinite(value) ? value : 0);
  }, 0);

  if (!tableNode) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[2100] overflow-hidden">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative flex flex-col w-[80vw] h-[85vh] rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Table summary</h3>
            <div className="flex items-center gap-2">
              <button type="button" className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-gray-900 text-white" onClick={() => persist(addTableHeader(draft))}>+ Header</button>
              <button type="button" className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-gray-900 text-white" onClick={() => persist(addTableRow(draft, active.r))}>+ Row</button>
              <button type="button" className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-gray-900 text-white" onClick={() => persist(addTableColumn(draft, active.c))}>+ Column</button>
              <button type="button" className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200" onClick={() => persist(deleteTableRow(draft, active.r))}>- Row</button>
              <button type="button" className="px-2.5 py-1 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200" onClick={() => persist(deleteTableColumn(draft, active.c))}>- Column</button>
              <button type="button" className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none" onClick={onClose} aria-label="Close">×</button>
            </div>
          </div>
          <div className="flex flex-1 min-h-0">
            <div className="flex-[8] min-w-0 overflow-auto p-4">
              <table className="border-collapse text-sm" style={{ width: tableWidth, tableLayout: 'fixed' }}>
                <colgroup>
                  {colWidths.map((width, colIndex) => (
                    <col key={`col-${colIndex}`} style={{ width }} />
                  ))}
                </colgroup>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={`r-${rowIndex}`}>
                      {Array.from({ length: colCount }, (_, colIndex) => {
                        const value = row[colIndex] || '';
                        const check = parseCheckCell(value);
                        const isHeader = hasHeader && rowIndex === 0;
                        const Tag = isHeader ? 'th' : 'td';
                        return (
                          <Tag
                            key={`c-${rowIndex}-${colIndex}`}
                            className={`relative border border-gray-200 dark:border-gray-600 p-1 align-top ${isHeader ? 'bg-gray-100 dark:bg-gray-900' : ''} ${active.r === rowIndex && active.c === colIndex ? 'ring-2 ring-indigo-500' : ''}`}
                            style={{ width: colWidths[colIndex] }}
                            onMouseDown={() => setActive({ r: rowIndex, c: colIndex })}
                          >
                            {check ? (
                              <label className={`flex items-center gap-2 rounded-md px-1 py-0.5 ${check.checked ? 'bg-green-400/80 text-green-950' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={check.checked}
                                  onChange={(event) => updateCell(rowIndex, colIndex, encodeCheckCell(event.target.checked, check.text))}
                                />
                                <input
                                  value={check.text}
                                  onChange={(event) => updateCell(rowIndex, colIndex, encodeCheckCell(check.checked, event.target.value))}
                                  className="min-w-0 flex-1 bg-transparent outline-none text-sm"
                                />
                              </label>
                            ) : (
                              <input
                                value={value}
                                onChange={(event) => updateCell(rowIndex, colIndex, event.target.value)}
                                className={`w-full bg-transparent outline-none px-1 py-1 text-sm text-gray-900 dark:text-white ${isHeader ? 'font-semibold' : ''}`}
                                placeholder={isHeader ? `Header ${colIndex + 1}` : ''}
                              />
                            )}
                            {rowIndex === 0 ? (
                              <span
                                className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10"
                                onMouseDown={(event) => startResize(event, colIndex)}
                              />
                            ) : null}
                          </Tag>
                        );
                      })}
                    </tr>
                  ))}
                  <tr>
                    {Array.from({ length: colCount }, (_, colIndex) => {
                      const metric = summaryColumns[String(colIndex)];
                      const col = summary.columns[colIndex];
                      const value = metric ? columnMetricValue(col, metric) : null;
                      return (
                        <td key={`sum-${colIndex}`} className="border border-gray-200 dark:border-gray-600 bg-indigo-50 dark:bg-indigo-950/40 px-2 py-2 text-xs font-semibold text-indigo-800 dark:text-indigo-200">
                          {metric ? `${SUMMARY_METRICS.find((item) => item.id === metric)?.label || metric}: ${formatNumber(value)}` : '—'}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
              <p className="mt-3 text-xs text-gray-500">
                {summary.rowCount} rows · {summary.colCount} columns
                {hasHeader ? ' · header row on' : ' · use + Header to add a header row'}
                {' · drag a column edge to resize'}
              </p>
            </div>
            <aside className="flex-[1] min-w-[180px] max-w-[240px] w-[10%] border-l border-gray-200 dark:border-gray-700 overflow-y-auto p-3 space-y-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Summary</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Choose which columns to total.</p>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/60 px-2 py-2 text-xs">
                <div className="text-gray-500">Applied sum</div>
                <div className="font-semibold text-gray-900 dark:text-white">{formatNumber(appliedGrand)}</div>
              </div>
              {summary.columns.map((col) => {
                const key = String(col.index);
                const enabled = Boolean(summaryColumns[key]);
                const metric = summaryColumns[key] || 'sum';
                return (
                  <div key={key} className="rounded-lg border border-gray-200 dark:border-gray-700 p-2 space-y-1.5">
                    <label className="flex items-start gap-2 text-xs text-gray-800 dark:text-gray-200">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={enabled}
                        onChange={(event) => toggleColumn(col.index, event.target.checked)}
                      />
                      <span className="font-semibold leading-4 break-words">{col.header || `Column ${col.index + 1}`}</span>
                    </label>
                    <select
                      value={metric}
                      disabled={!enabled}
                      onChange={(event) => setColumnMetric(col.index, event.target.value)}
                      className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-1.5 py-1 text-[11px] text-gray-800 dark:text-gray-200 disabled:opacity-50"
                    >
                      {SUMMARY_METRICS.map((item) => (
                        <option key={item.id} value={item.id}>{item.label}</option>
                      ))}
                    </select>
                    {enabled ? (
                      <div className="text-[11px] text-gray-500">
                        {SUMMARY_METRICS.find((item) => item.id === metric)?.label}: {formatNumber(columnMetricValue(col, metric))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </aside>
          </div>
          <div className="flex justify-end px-5 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default NoteTableWorkspaceModal;
